/**
 * OPUS-913 — reusable list/data states + PreviewSurface.
 * Ported from the Claude Design handoff (app/states.jsx). Every list/data view
 * uses these for content/empty/loading/error/locked, each explaining why + a
 * next step. UI gating mirrors backend permissions.
 */
import React from "react";
import { Icon } from "./Icon";
import { Button } from "./Button";
import { Badge, BADGE_TONES } from "./Badge";

export function Skeleton({ width = "100%", height = 14, radius = 6, style = {} }) {
  return <div className="opus-skeleton" style={{ width, height, borderRadius: radius, ...style }} />;
}

export function StateShell({ icon, iconTone = "neutral", title, message, action, secondary, dense = false }) {
  const t = BADGE_TONES[iconTone] || BADGE_TONES.neutral;
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", padding: dense ? "36px 24px" : "60px 32px", gap: 4 }}>
      <div style={{ width: 56, height: 56, borderRadius: "var(--r-lg)", background: t.bg, color: t.color, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16 }}>
        <Icon name={icon} size={26} />
      </div>
      <h3 style={{ fontSize: 16.5, fontWeight: 700, color: "var(--text)", letterSpacing: "-0.01em" }}>{title}</h3>
      {message && <p style={{ fontSize: 13.5, color: "var(--text-muted)", maxWidth: 380, lineHeight: 1.5, marginTop: 4 }}>{message}</p>}
      {(action || secondary) && <div style={{ display: "flex", gap: 10, marginTop: 18 }}>{secondary}{action}</div>}
    </div>
  );
}

export function EmptyState({ title = "Nothing here yet", message, action, icon = "layers", dense }) {
  return <StateShell icon={icon} iconTone="neutral" title={title} message={message} action={action} dense={dense} />;
}

export function LoadingState({ rows = 5, label = "Loading…" }) {
  return (
    <div style={{ padding: "8px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 4px 16px", color: "var(--text-dim)", fontSize: 12.5 }}>
        <span className="opus-spin" style={{ display: "inline-flex" }}><Icon name="refresh" size={14} /></span>{label}
      </div>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} style={{ display: "flex", alignItems: "center", gap: 14, padding: "14px 4px", borderTop: i ? "1px solid var(--border)" : "none" }}>
          <Skeleton width={36} height={36} radius={8} />
          <Skeleton width={`${30 + (i % 3) * 12}%`} height={13} />
          <Skeleton width={90} height={13} style={{ marginLeft: "auto" }} />
          <Skeleton width={64} height={22} radius={999} />
        </div>
      ))}
    </div>
  );
}

/**
 * Card-grid loading skeleton — for pages that render a responsive grid of
 * cards/tiles (renderer grid, My Apps). The skeleton tiles mirror the real
 * card shape (optional 16:9 preview + icon + title/subtitle) so the lazy
 * placeholder matches the component it stands in for. Use LoadingState for
 * list/table pages and CardGridSkeleton for grids — one loader language.
 */
export function CardGridSkeleton({ count = 8, minWidth = 240, preview = true, label = "Loading…" }) {
  return (
    <div style={{ padding: "8px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 9, padding: "0 4px 16px", color: "var(--text-dim)", fontSize: 12.5 }}>
        <span className="opus-spin" style={{ display: "inline-flex" }}><Icon name="refresh" size={14} /></span>{label}
      </div>
      <div style={{ display: "grid", gridTemplateColumns: `repeat(auto-fill, minmax(${minWidth}px, 1fr))`, gap: 16 }}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} style={{ display: "flex", flexDirection: "column", gap: 12, padding: 14, border: "1px solid var(--border)", borderRadius: "var(--r-lg)", background: "var(--surface-1)" }}>
            {preview && <Skeleton width="100%" height={140} radius={10} />}
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Skeleton width={36} height={36} radius={8} />
              <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 8 }}>
                <Skeleton width={`${55 + (i % 3) * 12}%`} height={13} />
                <Skeleton width="42%" height={11} />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export function ErrorState({ title = "Something went wrong", message = "We couldn't load this data. Check your connection and try again.", onRetry, dense }) {
  return (
    <StateShell icon="alertTriangle" iconTone="coral" title={title} message={message} dense={dense}
      action={onRetry && <Button variant="secondary" icon="refresh" onClick={onRetry}>Retry</Button>} />
  );
}

export function AccessDeniedState({ message = "You don't have permission to view this area. Ask a Super Admin to grant access.", dense, onRequest }) {
  return (
    <StateShell icon="shield" iconTone="coral" title="Access denied" message={message} dense={dense}
      action={<Button variant="secondary" icon="external" onClick={onRequest}>Request access</Button>} />
  );
}

const LOCK_COPY = {
  role: { title: "Locked by role", msg: "Your current role can't use this feature. An Org Admin can change your role." },
  entitlement: { title: "Not included in your plan", msg: "This feature isn't part of your organisation's entitlements. Contact an admin to enable it." },
  feature_flag: { title: "Coming soon", msg: "This feature is behind a flag and not yet enabled for your organisation." },
};
export function LockedFeatureState({ reason = "entitlement", dense, compact }) {
  const c = LOCK_COPY[reason] || LOCK_COPY.entitlement;
  if (compact) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", background: "var(--surface-2)", border: "1px solid var(--border)", borderRadius: "var(--r-md)" }}>
        <Icon name="lock" size={16} style={{ color: "var(--amber)" }} />
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text)" }}>{c.title}</div>
          <div style={{ fontSize: 12, color: "var(--text-dim)" }}>{c.msg}</div>
        </div>
      </div>
    );
  }
  return (
    <StateShell icon="lock" iconTone="amber" title={c.title} message={c.msg} dense={dense}
      action={<Button variant="secondary" icon="external">Contact admin</Button>} />
  );
}

/** 16:9 checker-backdrop preview surface with an optional bottom-left badge. */
export function PreviewSurface({ children, badge, style = {} }) {
  return (
    <div style={{
      position: "relative", aspectRatio: "16 / 9", width: "100%", borderRadius: "var(--r-md)",
      overflow: "hidden", border: "1px solid var(--border)",
      background: "repeating-conic-gradient(#1b1b1e 0% 25%, #161618 0% 50%) 50% / 22px 22px",
      display: "flex", alignItems: "center", justifyContent: "center", ...style,
    }}>
      {children}
      {badge && <div style={{ position: "absolute", left: 10, bottom: 10 }}>{badge}</div>}
    </div>
  );
}

export default StateShell;
